/**
 * Site-wide announcement modal.
 *
 * The dialog markup is always printed by the server; this module decides whether
 * to actually open it. A visitor's dismissals are stored in a single cookie that
 * holds the content fingerprints (`data-version`) of the announcements they have
 * already closed. When an editor changes the content the fingerprint changes, the
 * stored value stops matching and the modal is shown again.
 */

const COOKIE_NAME = 'forni_announcement';

/** How many past fingerprints to keep, so switching site language keeps its own dismissal. */
const MAX_STORED = 6;

const DAY_IN_SECONDS = 86400;
const DEFAULT_LIFETIME_DAYS = 365;

/** Exit-intent cannot happen on touch devices, so fall back to a timer there. */
const TOUCH_EXIT_FALLBACK_MS = 20000;

function readDismissed() {
	const match = document.cookie.match(
		new RegExp( '(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)' )
	);

	if ( ! match ) {
		return [];
	}

	try {
		return decodeURIComponent( match[ 1 ] ).split( ',' ).filter( Boolean );
	} catch ( e ) {
		return [];
	}
}

function writeDismissed( versions, maxAgeSeconds ) {
	const value = encodeURIComponent(
		versions.slice( 0, MAX_STORED ).join( ',' )
	);

	let cookie = COOKIE_NAME + '=' + value + '; path=/; SameSite=Lax';

	// No max-age at all makes it a session cookie.
	if ( maxAgeSeconds !== null ) {
		cookie += '; max-age=' + maxAgeSeconds;
	}

	if ( window.location.protocol === 'https:' ) {
		cookie += '; Secure';
	}

	document.cookie = cookie;
}

class AnnouncementModal {
	constructor() {
		this.el = document.getElementById( 'forni-announcement' );
		this.backdrop = null;
		this.isOpen = false;
		this.isRemembered = false;
		this.usesFallback = false;
		this.timers = [];
		this.teardown = [];
	}

	init() {
		if ( ! this.el ) {
			return;
		}

		this.version = this.el.dataset.version || '';
		this.isPreview = this.el.dataset.preview === '1';

		if ( ! this.version ) {
			return;
		}

		if ( ! this.isPreview && readDismissed().includes( this.version ) ) {
			return;
		}

		this.bindDialog();
		this.armTrigger();
	}

	/* --------------------------------------------------------------------
	 * Wiring
	 * ----------------------------------------------------------------- */

	bindDialog() {
		const closeBtn = this.el.querySelector(
			'.js-forni-announcement-close'
		);

		if ( closeBtn ) {
			closeBtn.addEventListener( 'click', () => this.close() );
		}

		// Following the call to action counts as having seen the announcement.
		const cta = this.el.querySelector( '.js-forni-announcement-cta' );

		if ( cta ) {
			cta.addEventListener( 'click', () => this.remember() );
		}

		// Light dismiss: a click that lands on the dialog box itself is a click
		// on the backdrop, because every visible part is a child element.
		this.el.addEventListener( 'click', ( event ) => {
			if ( event.target === this.el ) {
				this.close();
			}
		} );

		// Fires for the close button, backdrop clicks and the Esc key alike.
		this.el.addEventListener( 'close', () => this.handleClosed() );
	}

	armTrigger() {
		const trigger = this.el.dataset.trigger || 'delay';

		switch ( trigger ) {
			case 'immediate':
				window.requestAnimationFrame( () => this.open() );
				break;

			case 'scroll':
				this.armScroll();
				break;

			case 'exit':
				this.armExitIntent();
				break;

			case 'delay':
			default:
				this.armDelay();
				break;
		}
	}

	armDelay() {
		const seconds = parseInt( this.el.dataset.delay, 10 );
		const delay = Number.isNaN( seconds ) ? 3 : Math.max( 0, seconds );

		this.timers.push(
			window.setTimeout( () => this.open(), delay * 1000 )
		);
	}

	armScroll() {
		const parsed = parseInt( this.el.dataset.scroll, 10 );
		const threshold = Number.isNaN( parsed )
			? 40
			: Math.min( 100, Math.max( 1, parsed ) );

		const onScroll = () => {
			const scrollable =
				document.documentElement.scrollHeight - window.innerHeight;

			// A page too short to scroll counts as fully read.
			const percent =
				scrollable > 0 ? ( window.scrollY / scrollable ) * 100 : 100;

			if ( percent >= threshold ) {
				this.open();
			}
		};

		window.addEventListener( 'scroll', onScroll, { passive: true } );
		this.teardown.push( () =>
			window.removeEventListener( 'scroll', onScroll )
		);

		onScroll();
	}

	armExitIntent() {
		const canHover = window.matchMedia( '(pointer: fine)' ).matches;

		if ( ! canHover ) {
			this.timers.push(
				window.setTimeout( () => this.open(), TOUCH_EXIT_FALLBACK_MS )
			);
			return;
		}

		const onMouseOut = ( event ) => {
			if ( event.clientY <= 0 && ! event.relatedTarget ) {
				this.open();
			}
		};

		document.addEventListener( 'mouseout', onMouseOut );
		this.teardown.push( () =>
			document.removeEventListener( 'mouseout', onMouseOut )
		);
	}

	disarmTriggers() {
		this.timers.forEach( ( id ) => window.clearTimeout( id ) );
		this.timers = [];
		this.teardown.forEach( ( fn ) => fn() );
		this.teardown = [];
	}

	/* --------------------------------------------------------------------
	 * Open / close
	 * ----------------------------------------------------------------- */

	open() {
		if ( this.isOpen ) {
			return;
		}

		this.isOpen = true;
		this.disarmTriggers();

		if ( typeof this.el.showModal === 'function' ) {
			try {
				this.el.showModal();
			} catch ( e ) {
				this.openFallback();
			}
		} else {
			this.openFallback();
		}

		document.documentElement.classList.add( 'forni-announcement-open' );
	}

	/** Browsers without a top layer: emulate the modal with our own backdrop. */
	openFallback() {
		this.usesFallback = true;
		this.el.classList.add( 'forni-announcement--fallback' );
		this.el.setAttribute( 'open', '' );
		this.el.setAttribute( 'tabindex', '-1' );

		this.backdrop = document.createElement( 'div' );
		this.backdrop.className = 'forni-announcement-backdrop';
		this.backdrop.addEventListener( 'click', () => this.close() );
		document.body.appendChild( this.backdrop );

		this.onEscape = ( event ) => {
			if ( event.key === 'Escape' ) {
				this.close();
			}
		};

		document.addEventListener( 'keydown', this.onEscape );
		this.el.focus();
	}

	close() {
		if ( ! this.isOpen ) {
			return;
		}

		if ( this.usesFallback ) {
			this.el.removeAttribute( 'open' );
			this.handleClosed();
			return;
		}

		this.el.close();
	}

	handleClosed() {
		if ( ! this.isOpen ) {
			return;
		}

		this.isOpen = false;
		this.remember();

		document.documentElement.classList.remove( 'forni-announcement-open' );

		if ( this.backdrop ) {
			this.backdrop.remove();
			this.backdrop = null;
		}

		if ( this.onEscape ) {
			document.removeEventListener( 'keydown', this.onEscape );
			this.onEscape = null;
		}
	}

	/* --------------------------------------------------------------------
	 * Dismissal cookie
	 * ----------------------------------------------------------------- */

	remember() {
		if ( this.isRemembered || this.isPreview ) {
			return;
		}

		this.isRemembered = true;

		const versions = readDismissed().filter( ( v ) => v !== this.version );
		versions.unshift( this.version );

		writeDismissed( versions, this.getLifetime() );
	}

	/** Seconds the cookie should live for, or null for a session cookie. */
	getLifetime() {
		const mode = this.el.dataset.reshow || 'once';

		if ( mode === 'session' ) {
			return null;
		}

		if ( mode === 'days' ) {
			const days = parseInt( this.el.dataset.reshowDays, 10 );
			return (
				( Number.isNaN( days ) ? 7 : Math.max( 1, days ) ) *
				DAY_IN_SECONDS
			);
		}

		return DEFAULT_LIFETIME_DAYS * DAY_IN_SECONDS;
	}
}

export default AnnouncementModal;
